import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { defaultSiteConfig } from '../data/defaultConfig';
import { getApiBase } from '../lib/apiBase';
import type { SiteConfig } from '../types/cms';

function deepMerge<T>(target: T, source: Partial<T>): T {
  const result = JSON.parse(JSON.stringify(target)) as T;
  for (const key in source) {
    const srcVal = source[key];
    const tgtVal = result[key];
    if (
      srcVal &&
      typeof srcVal === 'object' &&
      !Array.isArray(srcVal) &&
      tgtVal &&
      typeof tgtVal === 'object' &&
      !Array.isArray(tgtVal)
    ) {
      result[key] = deepMerge(tgtVal, srcVal) as T[Extract<keyof T, string>];
    } else if (srcVal !== undefined) {
      result[key] = srcVal as T[Extract<keyof T, string>];
    }
  }
  return result;
}

export interface CMSContextValue {
  config: SiteConfig;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const CMSContext = createContext<CMSContextValue>({
  config: defaultSiteConfig,
  loading: true,
  error: null,
  refetch: async () => {},
});

export function CMSProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SiteConfig>(defaultSiteConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const base = getApiBase();
      const res = await fetch(`${base}/api/cms/data?t=${Date.now()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as Partial<SiteConfig>;
      setConfig(deepMerge(defaultSiteConfig, data));
    } catch (err) {
      console.error('CMS fetch failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setConfig(defaultSiteConfig);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  return (
    <CMSContext.Provider value={{ config, loading, error, refetch: fetchConfig }}>
      {children}
    </CMSContext.Provider>
  );
}

export function useCMS(): CMSContextValue {
  return useContext(CMSContext);
}
